/*
    INFOS:
    Discussed on this ticket: https://support.dfau.de/#ticket/zoom/16001

    What page_type?
    1. 'home'           if page is "/"
    2. 'blog'           if on /blog or /blog/... (different installation)
    3. 'checkout'       if we find the data-gtm-selector="checkout-page" on the page
    4. 'purchase'       if we find the data-gtm-selector="purchase-page" on the page
    5. 'PDP'            if the page-doktype is 20, 24 or 26
    6. 'main_category'  if on /reiseziele
    7. 'sub_category1'  all pages not under /reiseziele + /reiseziele/reisefinder
    8. 'destination'    if a continent, a region or country under /reiseziele
*/

const PRODUCT_PAGE_DOKTYPES = ['20', '24', '26'];
const CHECKOUT_PAGE_SELECTOR = '[data-gmt-selector="checkout-page"]';
const PURCHASE_PAGE_SELECTOR = '[data-gmt-selector="purchase-page"]';
const PRODUCT_CODE_SELECTOR = '.trip-page__sidebar-code span';
const PRODUCT_PRICE_SELECTOR = '.trip__price';
const NOT_AVAILABLE_TEXT = 'n/A';
const PAGE_VIEW_PAGE_TYPES = {
    DEFAULT: 'generic',
    HOME: 'home',
    BLOG: 'blog',
    PRODUCT: 'PDP',
    MAIN_CATEGORY: 'main_category',
    SUB_CATEGORY: 'sub_category1',
    DESTINATION: 'destination',
    CHECKOUT: 'checkout',
    PURCHASE: 'purchase',
}

function initPageView() {
    const breadcrumbLinks = document.querySelectorAll('.breadcrumb__list a');
    const breadcrumbTexts = breadcrumbLinks
        ? Array.from(breadcrumbLinks).map(link => link.textContent.trim())
        : [];

    const pageType = getPageType(breadcrumbLinks);
    const eventData = {
        event: 'page_view',
        event_name: 'page_view',
        page_type: pageType || PAGE_VIEW_PAGE_TYPES.DEFAULT,
        category_tree:  pageType == PAGE_VIEW_PAGE_TYPES.HOME ? PAGE_VIEW_PAGE_TYPES.HOME : getCategoryTree(breadcrumbTexts),
        main_category_name:  getMainCategory(breadcrumbTexts),
        sub_category_name: getSubCategory(pageType, breadcrumbTexts),
        region: getRegion(pageType, breadcrumbTexts),
        reiseLand: getCountry(pageType, breadcrumbTexts),
    }

    if (PAGE_VIEW_PAGE_TYPES.PRODUCT) {
        eventData.product_name = getProductName(pageType, breadcrumbTexts);
        eventData.product_id = getProductId();
        eventData.price = getProductPrice();
    }

    dispatchGtmEvent(eventData);
}

/**
 * Determines the page type based on breadcrumb links and other page attributes.
 *
 * @param {HTMLElement[]} breadcrumbLinks - An array of breadcrumb link elements.
 * @returns {string} - The determined page type. Possible values are defined in `PAGE_VIEW_PAGE_TYPES`.
 */
function getPageType(breadcrumbLinks) {
    const pageDoktype = document.documentElement.getAttribute('page-doktype');

    const isCheckoutPage = document.querySelector(CHECKOUT_PAGE_SELECTOR);
    const isPurchasePage = document.querySelector(PURCHASE_PAGE_SELECTOR);

    const firstBreadcrumb = breadcrumbLinks.length > 0 ? breadcrumbLinks[0].textContent.trim() : '';
    const secondBreadcrumb = breadcrumbLinks.length > 1 ? breadcrumbLinks[1].textContent.trim() : '';

    if (window.location.pathname === '/') return PAGE_VIEW_PAGE_TYPES.HOME;

    if (window.location.pathname.includes('/blog')) return PAGE_VIEW_PAGE_TYPES.BLOG;

    if (PRODUCT_PAGE_DOKTYPES.includes(pageDoktype)) return PAGE_VIEW_PAGE_TYPES.PRODUCT;

    if (isCheckoutPage) return PAGE_VIEW_PAGE_TYPES.CHECKOUT;

    if (isPurchasePage) return PAGE_VIEW_PAGE_TYPES.PURCHASE;

    if (breadcrumbLinks.length === 1) return PAGE_VIEW_PAGE_TYPES.MAIN_CATEGORY;

    if (firstBreadcrumb === 'Reiseziele' && secondBreadcrumb && secondBreadcrumb !== 'Reisefinder') {
        return PAGE_VIEW_PAGE_TYPES.DESTINATION;
    }

    if ((secondBreadcrumb && secondBreadcrumb === 'Reisefinder') || (firstBreadcrumb && firstBreadcrumb !== 'Reiseziele')) {
        return PAGE_VIEW_PAGE_TYPES.SUB_CATEGORY;
    }

    return PAGE_VIEW_PAGE_TYPES.DEFAULT;
}

/**
 * Generates a category tree string from an array of breadcrumb texts.
 *
 * @param {string[]} breadcrumbTexts - An array of breadcrumb texts.
 * @returns {string} A formatted category tree string, or "n/A" if no breadcrumbs are available.
 */
function getCategoryTree(breadcrumbTexts) {
    return breadcrumbTexts.length ? breadcrumbTexts.join(' > ') : NOT_AVAILABLE_TEXT;
}

/**
 * Retrieves the main category from breadcrumb texts.
 *
 * @param {string[]} breadcrumbTexts - An array of breadcrumb texts.
 * @returns {string} The first breadcrumb text, or a default "not available" text if none exist.
 */
function getMainCategory(breadcrumbTexts) {
    return breadcrumbTexts[0] || NOT_AVAILABLE_TEXT;
}

/**
 * Retrieves the desired breadcrumb text based on the page type and breadcrumb index.
 *
 * @param {string} pageType - The current page type.
 * @param {string[]} breadcrumbTexts - An array of breadcrumb texts.
 * @param {string[]} expectedPageTypes - An array of page types to match.
 * @param {number} index - The index of the breadcrumb to retrieve.
 * @returns {string} The breadcrumb text at the specified index, or a default "not available" text if not applicable.
 */
function getBreadcrumbText(pageType, breadcrumbTexts, expectedPageTypes, index) {
    if (expectedPageTypes.includes(pageType)) {
        return breadcrumbTexts[index] || NOT_AVAILABLE_TEXT;
    }

    return NOT_AVAILABLE_TEXT;}

/**
 * Retrieves the sub-category from breadcrumb texts if the page type is a sub-category.
 *
 * @param {string} pageType - The current page type.
 * @param {string[]} breadcrumbTexts - An array of breadcrumb texts.
 * @returns {string} The sub-category, or a default "not available" text if not applicable.
 */
function getSubCategory(pageType, breadcrumbTexts) {
    return getBreadcrumbText(pageType, breadcrumbTexts, PAGE_VIEW_PAGE_TYPES.SUB_CATEGORY, 1);
}

/**
 * Retrieves the region from breadcrumb texts if the page type is a destination.
 *
 * @param {string} pageType - The current page type.
 * @param {string[]} breadcrumbTexts - An array of breadcrumb texts.
 * @returns {string} The region, or a default "not available" text if not applicable.
 */
function getRegion(pageType, breadcrumbTexts) {
    return getBreadcrumbText(
        pageType,
        breadcrumbTexts,
        [PAGE_VIEW_PAGE_TYPES.DESTINATION, PAGE_VIEW_PAGE_TYPES.PRODUCT],
        1
    );
}

/**
 * Retrieves the country from breadcrumb texts if the page type is a destination.
 *
 * @param {string} pageType - The current page type.
 * @param {string[]} breadcrumbTexts - An array of breadcrumb texts.
 * @returns {string} The country, or a default "not available" text if not applicable.
 */
function getCountry(pageType, breadcrumbTexts) {
    return getBreadcrumbText(
        pageType,
        breadcrumbTexts,
        [PAGE_VIEW_PAGE_TYPES.DESTINATION, PAGE_VIEW_PAGE_TYPES.PRODUCT],
        2
    );
}

/**
 * Retrieves the product name from breadcrumb texts if the page type is a product.
 *
 * @param {string} pageType - The current page type.
 * @param {string[]} breadcrumbTexts - An array of breadcrumb texts.
 * @returns {string} The product name, or a default "not available" text if not applicable.
 */
function getProductName(pageType, breadcrumbTexts) {
    return getBreadcrumbText(pageType, breadcrumbTexts, PAGE_VIEW_PAGE_TYPES.PRODUCT, 3);
}

/**
 * Retrieves the product ID from the page if available.
 * If the product ID is not found or is empty, returns a default "not available" text.
 *
 * @returns {string} The product ID, or a default "not available" text if not found.
 */
function getProductId() {
    const productCodeElement = document.querySelector(PRODUCT_CODE_SELECTOR);
    return productCodeElement && productCodeElement.textContent.trim() || NOT_AVAILABLE_TEXT;
}

/**
 * Retrieves the product price from the page if available.
 * If the product price is not found or is empty, returns a default "not available" text.
 *
 * @returns {string} The product price, or a default "not available" text if not found.
 */
function getProductPrice() {
    const productPriceElement = document.querySelector(PRODUCT_PRICE_SELECTOR);
    if (productPriceElement) {
        return parseInt(
            productPriceElement.textContent.replace(/\D/g, '') // Remove all non-numeric characters
            ) || NOT_AVAILABLE_TEXT;
    }
    return NOT_AVAILABLE_TEXT;
}

// Ensure initPageView runs after the DOM is ready or immediately if already ready
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    requestAnimationFrame(initPageView);
} else {
    document.addEventListener('DOMContentLoaded', initPageView, { once: true });
}
;
define("skr/tracking/pageView", function(){});

